import React, { useCallback, useMemo, useState } from "react";
import {
  Panel,
  PanelHeader,
  PanelContent,
  PanelFooter,
  PanelFooterColumn,
  Button,
  ButtonGroup,
  Popup,
  PopupSection,
  Heading,
} from "@flixbus/honeycomb-react";
import { IconArrowLeft } from "@flixbus/honeycomb-icons-react";
import { panelId } from "../CostPanel.constants";
import EditDimensionValuesView from "./EditDimensionValuesView/EditDimensionValuesView.component";
import type { EditDimensionValuesRow } from "./EditDimensionValuesView/EditDimensionValuesView.types";
import type { DimensionEditState } from "../CostPanel.types";
import styles from "../CostPanel.module.scss";

interface EditDimensionValuesProps {
  active: boolean;
  onClose: () => void;
  subCategoryName: string;
  dimensionEditState: DimensionEditState;
  dimensionValues: string[];
  onCancel: () => void;
  onSubmit: (values: string[]) => void;
  isSubmitting: boolean;
}

const newRow = (value = ""): EditDimensionValuesRow => ({
  id: crypto.randomUUID(),
  value,
});

const seedRows = (values: string[]): EditDimensionValuesRow[] =>
  values.length > 0 ? values.map((v) => newRow(v)) : [newRow()];

const EditDimensionValues: React.FC<EditDimensionValuesProps> = ({
  active,
  onClose,
  subCategoryName,
  dimensionEditState,
  dimensionValues,
  onCancel,
  onSubmit,
  isSubmitting,
}) => {
  const [rows, setRows] = useState<EditDimensionValuesRow[]>(() =>
    seedRows(dimensionValues),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [invalidRowIds, setInvalidRowIds] = useState<Set<string>>(new Set());

  const stagedValues = useMemo(
    () => rows.map((r) => r.value).filter((v) => v !== ""),
    [rows],
  );
  const removedValues = useMemo(
    () => dimensionValues.filter((v) => !stagedValues.includes(v)),
    [dimensionValues, stagedValues],
  );
  const isUnchanged = useMemo(() => {
    if (stagedValues.length !== dimensionValues.length) return false;
    const set = new Set(stagedValues);
    return dimensionValues.every((v) => set.has(v));
  }, [stagedValues, dimensionValues]);

  const handleChangeRow = useCallback((id: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, value } : r)));
    setInvalidRowIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, newRow()]);
  }, []);
  const handleRemoveRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const hasEmptyRows = useMemo(() => rows.some((r) => r.value === ""), [rows]);

  const handleUpdate = () => {
    if (hasEmptyRows) {
      setInvalidRowIds(
        new Set(rows.filter((r) => r.value === "").map((r) => r.id)),
      );
      return;
    }
    setInvalidRowIds(new Set());
    if (removedValues.length > 0) {
      setConfirmOpen(true);
      return;
    }
    onSubmit(stagedValues);
  };
  const handleConfirmRemoval = () => {
    setConfirmOpen(false);
    onSubmit(stagedValues);
  };

  const typeName = dimensionEditState.dimensionTypeName;
  const lowerTypeName = typeName.toLowerCase();
  const title = [`Edit ${typeName}`, subCategoryName]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <Panel id={panelId} active={active} extraClasses={styles.panelWrapper}>
        <PanelHeader
          backButtonProps={{
            label: "Back",
            onClick: onCancel,
            InlineIcon: IconArrowLeft,
          }}
          closeButtonProps={{ label: "Close", onClick: onClose }}
        >
          <Heading size={3} sectionHeader flushSpace>
            {title}
          </Heading>
        </PanelHeader>
        <PanelContent>
          <EditDimensionValuesView
            dimensionTypeName={dimensionEditState.dimensionTypeName}
            seededValues={dimensionEditState.seededValues}
            rows={rows}
            onChangeRow={handleChangeRow}
            onAddRow={handleAddRow}
            onRemoveRow={handleRemoveRow}
            invalidRowIds={invalidRowIds}
          />
        </PanelContent>
        <PanelFooter extraClasses={styles.footer}>
          <PanelFooterColumn>
            <ButtonGroup>
              <Button display="block" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                display="block"
                loading={isSubmitting}
                disabled={isSubmitting || (isUnchanged && !hasEmptyRows)}
                onClick={handleUpdate}
              >
                Update
              </Button>
            </ButtonGroup>
          </PanelFooterColumn>
        </PanelFooter>
      </Panel>
      <Popup
        active={confirmOpen}
        onOverlayClick={() => setConfirmOpen(false)}
        extraClasses={styles.confirmPopup}
      >
        <PopupSection type="title">{`Remove ${lowerTypeName} values?`}</PopupSection>
        <PopupSection type="content">
          {`Removing ${removedValues.join(", ")} will delete each bucket's stored inputs, formula overrides, and result data.`}
        </PopupSection>
        <PopupSection type="actions">
          <Button appearance="secondary" onClick={() => setConfirmOpen(false)}>
            Keep values
          </Button>
          <Button appearance="danger" onClick={handleConfirmRemoval}>
            Remove
          </Button>
        </PopupSection>
      </Popup>
    </>
  );
};

export default EditDimensionValues;
